/**
 * Stale-deploy recovery.
 *
 * Every page is a lazy chunk (see router.tsx) and every build stamps new asset
 * hashes. A tab left open across a deploy therefore holds an `index.html` whose
 * chunk URLs no longer exist on the CDN, and the next navigation fails with
 * "Unable to preload CSS for /assets/MarketingLayout-<hash>.css" — reported from
 * production on 2026-08-08. Nothing is broken; the tab is simply out of date.
 *
 * The fix is a reload, but a reload that can loop is worse than the error it
 * replaces: if the asset is genuinely missing (a bad deploy, a half-invalidated
 * CDN), an unguarded handler spins the browser forever. So a reload is attempted
 * exactly ONCE per tab, marked in sessionStorage, and the marker is cleared once
 * a load survives — which makes the next deploy recoverable too.
 */

/** sessionStorage key holding WHEN this tab last retried a stale chunk. */
export const STALE_RELOAD_KEY = 'pc-stale-deploy-reload';

/**
 * How long one retry suppresses the next. Sized by the two cases it separates:
 *
 * - A reload that did NOT fix the staleness (the CDN is still handing out the
 *   old `index.html`, or the asset is genuinely gone) fails again within a
 *   second or two. Every such repeat lands inside this window and is refused,
 *   so the tab settles on the error page instead of spinning.
 * - A genuinely new incident is the NEXT deploy, hours later. That falls
 *   outside the window and recovers automatically like the first one did.
 *
 * An hour is therefore deliberate, not arbitrary: a shorter window (say ten
 * seconds) still permits a reload every ten seconds indefinitely, which is a
 * slower spin rather than a fix.
 *
 * A timestamp rather than a plain flag, because an earlier version cleared the
 * flag on the window `load` event — which fires on the reloaded page BEFORE the
 * router re-requests the missing chunk, so the guard reset itself every attempt
 * and "reload once" became reload forever.
 */
export const RELOAD_SUPPRESSION_MS = 60 * 60 * 1000;

/** The shapes we need from `window`, so the behaviour is testable without one. */
export interface ReloadTarget {
  sessionStorage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  location: { reload: () => void };
}

/**
 * Does this error look like a chunk/asset that is no longer on the CDN, rather
 * than a fault in our own code? Vite's own preload rejection, and the dynamic
 * import failures browsers word differently, all land here.
 */
export function isStaleChunkError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : typeof error === 'object' && error !== null && 'message' in error
          ? String((error as { message: unknown }).message)
          : '';
  if (message === '') return false;
  return (
    /unable to preload/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message) ||
    /dynamically imported module.*(404|not found)/i.test(message)
  );
}

/**
 * Reload to pick up the current build. Returns true if a reload was started,
 * false if this tab just tried — in which case reloading again would loop and
 * the caller should let the error page show instead.
 */
export function reloadForStaleDeploy(target: ReloadTarget, now: number = Date.now()): boolean {
  try {
    const previous = target.sessionStorage.getItem(STALE_RELOAD_KEY);
    if (previous !== null) {
      const last = Number(previous);
      // An unparseable marker is treated as "just tried": something wrote a
      // value we do not understand, and looping is the worse failure.
      if (!Number.isFinite(last) || now - last < RELOAD_SUPPRESSION_MS) return false;
    }
    target.sessionStorage.setItem(STALE_RELOAD_KEY, String(now));
  } catch {
    // Private-mode or blocked storage: we cannot remember having tried, so a
    // reload could spin forever. Fail towards the visible error page, which
    // still offers a manual reload the person controls.
    return false;
  }
  target.location.reload();
  return true;
}

/**
 * Wire the Vite preload-failure event to the guarded reload. Called from the
 * entry point; safe to call once per document.
 *
 * Note there is deliberately NO `load` listener clearing the marker: the
 * reloaded page fires `load` before the router re-requests the chunk that
 * failed, so clearing there would reset the guard on every attempt. The
 * timestamp expiring is what allows a later, genuine deploy to recover.
 */
export function installStaleDeployRecovery(win: Window & ReloadTarget): void {
  win.addEventListener('vite:preloadError', (event) => {
    // Take over from Vite's default (which rethrows) only if we actually
    // reload — otherwise let the error surface to the route error boundary.
    if (reloadForStaleDeploy(win)) event.preventDefault();
  });
}
